import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <section className="relative bg-gradient-to-b from-white to-pink-50 overflow-hidden min-h-screen">
      {/* Organic curved background shapes matching Figma */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute top-0 left-0 w-full" viewBox="0 0 1440 320" fill="none">
          <path d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,138.7C960,139,1056,117,1152,106.7C1248,96,1344,96,1392,96L1440,96L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z" 
                fill="url(#gradient-pink)" fillOpacity="0.3"/>
          <defs>
            <linearGradient id="gradient-pink" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FEC5E5"/>
              <stop offset="100%" stopColor="#FADADD"/>
            </linearGradient>
          </defs>
        </svg>
        
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-pink-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-48 -left-24 w-96 h-96 bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-24 right-1/3 w-96 h-96 bg-yellow-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      {/* Navigation Bar - Matching Figma minimal style */}
      <nav className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="relative w-12 h-12 sm:w-14 sm:h-14">
              <img src="/TiflLogo.png" alt="TIFL Logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="text-2xl sm:text-3xl font-bold text-[#0C1D40] leading-none">Tifl</span>
              <span className="text-xs sm:text-sm text-pink-500 font-medium">طفل</span>
            </div>
          </div>
          
          <div className="hidden lg:flex items-center space-x-8">
            <a href="#features" className="text-[#0C1D40] hover:text-pink-500 transition-colors font-medium">Features</a>
            <a href="#milestones" className="text-[#0C1D40] hover:text-pink-500 transition-colors font-medium">Milestones</a>
            <a href="#goals" className="text-[#0C1D40] hover:text-pink-500 transition-colors font-medium">Goals</a>
            <a href="#community" className="text-[#0C1D40] hover:text-pink-500 transition-colors font-medium">Community</a>
            <button
              onClick={() => navigate('/splash')}
              className="bg-[#0C1D40] text-white px-8 py-3 rounded-full hover:bg-[#1A2B50] transition-all transform hover:scale-105 font-semibold"
            >
              Get Started
            </button>
          </div>
          
          {/* Mobile menu button */}
          <button 
            className="lg:hidden text-[#0C1D40] p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl rounded-b-2xl mt-2 py-4 px-4 space-y-4 border-t border-gray-100">
            <a href="#features" className="block text-[#0C1D40] hover:text-pink-500 transition-colors font-medium py-2">Features</a>
            <a href="#milestones" className="block text-[#0C1D40] hover:text-pink-500 transition-colors font-medium py-2">Milestones</a>
            <a href="#goals" className="block text-[#0C1D40] hover:text-pink-500 transition-colors font-medium py-2">Goals</a>
            <a href="#community" className="block text-[#0C1D40] hover:text-pink-500 transition-colors font-medium py-2">Community</a>
            <button
              onClick={() => navigate('/splash')}
              className="w-full bg-[#0C1D40] text-white px-8 py-3 rounded-full hover:bg-[#1A2B50] transition-all font-semibold"
            >
              Get Started
            </button>
          </div>
        )}
      </nav>

      {/* Hero Content - Matching Figma's friendly tone */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="text-center lg:text-left">
              {/* Friendly greeting badge */}
              <div className="inline-flex items-center bg-white rounded-full px-5 py-2 mb-6 shadow-sm border border-pink-100">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                <span className="text-sm font-medium text-[#0C1D40]">Hey Welcome! Track milestones daily</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="text-[#0C1D40]">Your Baby's First</span>
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10 text-[#0C1D40]">1,000 Days</span>
                  <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12">
                    <path d="M2 8C50 2 150 2 198 8" stroke="#FEC5E5" strokeWidth="4" strokeLinecap="round"/>
                  </svg>
                </span>
                <br />
                <span className="text-pink-500">Simplified</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 mb-10 leading-relaxed">
                Track developmental milestones, set personalized goals, and celebrate every achievement with Tifl — your trusted companion from pregnancy to age 3.
              </p>
              
              {/* Redesigned standalone CTA button */}
              <div className="flex justify-center lg:justify-start mb-8">
                <button 
                  onClick={() => navigate('/splash')}
                  className="group relative bg-gradient-to-r from-[#0C1D40] to-[#1A2B50] text-white px-12 py-5 rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105 font-bold text-lg"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    Get Started
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                    </svg>
                  </span>
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>
              
              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center lg:justify-start items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">14-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">Cancel anytime</span>
                </div>
              </div>
            </div>
            
            {/* Right Column - Illustration/Preview */}
            <div className="relative lg:pl-12">
              <div className="relative">
                {/* Phone mockup showcasing app */}
                <div className="relative mx-auto max-w-sm">
                  <div className="bg-white rounded-3xl shadow-2xl p-4 border border-gray-100">
                    <div className="bg-gradient-to-b from-pink-50 to-white rounded-2xl p-6">
                      {/* Mock app screen */}
                      <div className="space-y-4">
                        <div className="text-center mb-6">
                          <div className="w-20 h-20 bg-pink-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                            <span className="text-3xl">👶</span>
                          </div>
                          <h3 className="font-bold text-[#0C1D40]">Welcome to Tifl!</h3>
                          <p className="text-sm text-gray-600">Track your baby's journey</p>
                        </div>
                        
                        {/* Mock milestone cards */}
                        <div className="space-y-3">
                          <div className="bg-white rounded-xl p-3 shadow-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-[#0C1D40]">Motor Skills</span>
                              <span className="px-3 py-1 bg-[#0C1D40] text-white text-xs rounded-full">On Track</span>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-3 shadow-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-[#0C1D40]">Social Skills</span>
                              <span className="px-3 py-1 bg-pink-500 text-white text-xs rounded-full">Advanced</span>
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-3 shadow-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-[#0C1D40]">Emotional Skills</span>
                              <span className="px-3 py-1 bg-[#0C1D40] text-white text-xs rounded-full">On Track</span>
                            </div>
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-4 -left-4 bg-white rounded-xl shadow-lg p-3 hidden sm:block">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-sm">✓</span>
                    </div>
                    <span className="text-sm font-medium text-[#0C1D40]">Milestone achieved!</span>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-3 hidden sm:block">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🎯</span>
                    <div>
                      <p className="text-xs text-gray-500">Daily Goal</p>
                      <p className="text-sm font-bold text-[#0C1D40]">Tummy time: 5 mins</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;